import { setGlobalDateI18n } from 'fecha';
import { isDate } from '../type-checkers/type-checkers';

export const MS_PER_SECOND = 1000;
export const MS_PER_MINUTE = 60 * MS_PER_SECOND;
export const MS_PER_HOUR = 60 * MS_PER_MINUTE;
export const MS_PER_DAY = 24 * MS_PER_HOUR;

export enum Quarter {
    First = 1,
    Second,
    Third,
    Fourth
}

export const MAX_MONTH_RANGE = { START: 1, END: 31 };

export type DateRange = { firstDay: Date, lastDay: Date; };

export function getUnixTimestamp(date: Date): number {
    return Math.round(date.getTime() / 1000);
}

export function getStartOfDate(date: Date): Date {
    return isDate(date) ? new Date(date.setHours(0, 0, 0, 0)) : date;
}

export function getPreviousDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
}

export function getPreviousMonthRange(date: Date): DateRange {
    const previousMonthFirstDay = new Date(date.getFullYear(), date.getMonth() - 1);
    return getMonthRange(previousMonthFirstDay);
}

export function getNextMonthRange(date: Date): DateRange {
    const previousMonthFirstDay = new Date(date.getFullYear(), date.getMonth() + 1);
    return getMonthRange(previousMonthFirstDay);
}

export function getPreviousYearRange(day: Date): DateRange {
    return {
        firstDay: new Date(day.getFullYear() - 1, 0, 1),
        lastDay: new Date(day.getFullYear() - 1, 11, 31)
    };
}

export function getYearRange(day: Date): DateRange {
    return {
        firstDay: new Date(day.getFullYear(), 0, 1),
        lastDay: new Date(day.getFullYear(), 11, 31)
    };
}

export function getMonthRange(month: Date): DateRange {
    return {
        firstDay: new Date(month.getFullYear(), month.getMonth(), 1),
        lastDay: new Date(month.getFullYear(), month.getMonth() + 1, 0)
    };
}

export function addDays(date: Date, days: number): Date {
    const d = new Date(date.getTime());
    d.setDate(date.getDate() + days);
    return d;
}

export function substractDays(date: Date, days: number): Date {
    return addDays(date, -days);
}

export function addSeconds(date: Date, seconds: number): Date {
    const d = new Date(date.getTime());
    d.setSeconds(date.getSeconds() + seconds);
    return d;
}

export function substractSeconds(date: Date, seconds: number): Date {
    return addSeconds(date, -seconds);
}

export function dateDiffInMs(a: Date, b: Date): number {
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate(), a.getHours(), a.getMinutes(), a.getSeconds());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate(), b.getHours(), b.getMinutes(), b.getSeconds());

    return utc1 - utc2;
}

export function dateDiffInDays(a: Date, b: Date): number {
    // discard the time and time-zone information
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

    return Math.floor((utc1 - utc2) / MS_PER_DAY);
}

export function getDateWithCalculatedTimeZone(date: string | number | Date, mode: 'add' | 'substract' = 'add'): Date {
    const parsedDate = new Date(date);
    return mode === 'add' ? new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000) : new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
}

export function getMinimalOfDates(...dates: (Date | null | undefined)[]): Date | null {
    const datesNumber = dates
        .filter((d): d is Date => isDate(d))
        .map(date => date.getTime());

    return datesNumber.length ? new Date(Math.min(...datesNumber)) : null;
}

export function getMaxOfDates(...dates: Date[]): Date | null {
    return dates.length ? new Date(dates.reduce((a, b) => a >= b ? a : b).getTime()) : null;
}

export function getWeekList(): { label: string; value: number }[] {
    const upperCaseFirst = (stringValue: string) => {
        return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
    };

    const defaultI18nDateNames = setGlobalDateI18n({});
    const weekNames = defaultI18nDateNames.dayNames.map(upperCaseFirst);
    const sunday = weekNames.splice(0, 1)[0];
    weekNames.push(sunday); // place sunday at last position

    const weekList = weekNames.map((weekName, index) => ({
        label: weekName,
        value: index + 1
    }));

    return weekList;
}

// pattern: 08-10-2019 - 11:23:47
export function convertUTCDateToLocalDate(date: Date = new Date()): string {
    const pad = function (num: number) {
        const norm = Math.abs(Math.floor(num));
        return (norm < 10 ? '0' : '') + norm;
    };
    return date.getFullYear()
        + '-' + pad(date.getMonth() + 1)
        + '-' + pad(date.getDate())
        + 'T' + pad(date.getHours())
        + ':' + pad(date.getMinutes())
        + ':' + pad(date.getSeconds());
}

export function getQuarterNumber(date: Date): number {
    return Math.ceil((date.getMonth() + 1) / 3);
}

export function getQuarterRange(date: Date): DateRange {
    const startDate = getDateQuarterStartDate(date);
    return {
        firstDay: startDate,
        lastDay: new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0)
    };
}

export function getDateQuarterStartDate(date: Date): Date {
    return getQuaterStartDate(date, getQuarterNumber(date));
}

export function getYearQuarters(date: Date): {quarter: Quarter, startDate: Date}[] {
    return [Quarter.First, Quarter.Second, Quarter.Third, Quarter.Fourth].map(quater => ({
        quarter: quater,
        startDate: getQuaterStartDate(date, quater)
    }));
}

function getQuaterStartDate(date: Date, quater: Quarter) {
    return new Date(date.getFullYear(), (quater * 3) - 3, 1);
}

export type DateUnit = 'day' | 'month' | 'year';

/**
 * Checks if two dates are the same by a given unit (day, month, or year).
 * 
 * @example
 * ```typescript
 * const d1 = new Date(2022, 0, 1);
 * const d2 = new Date(2022, 0, 1);
 * isSameByUnit(d1, d2, 'day'); // true
 * isSameByUnit(d1, d2, 'month'); // true
 * isSameByUnit(d1, d2, 'year'); // true
 * ```
 * 
 * @param d1 The first date.
 * @param d2 The second date.
 * @param unit The unit to compare (day, month, or year).
 * @returns True if the dates are the same by the given unit, false otherwise.
 */
export function isSameDateByUnit(d1: Date, d2: Date, unit: DateUnit): boolean {
    const y = d1.getFullYear() === d2.getFullYear();
    const m = d1.getMonth() === d2.getMonth();
    const d = d1.getDate() === d2.getDate();

    if (unit === 'year') return y;
    if (unit === 'month') return y && m;
    return y && m && d;
}

/**
 * Checks if two dates are the same day.
 * 
 * @example
 * ```typescript
 * const d1 = new Date(2022, 0, 1);
 * const d2 = new Date(2022, 0, 1);
 * isSameDay(d1, d2); // true
 * ```
 * 
 * @param d1 The first date.
 * @param d2 The second date.
 * @returns True if the dates are the same day, false otherwise.
 */
export function isSameDay(d1: Date, d2: Date): boolean {
    return isSameDateByUnit(d1, d2, 'day');
}
